import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { arrayOf, bool, func, number, string } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';
import LiveWaveformStrip from './LiveWaveformStrip';
import PausedVoiceRecordingPreview from './PausedVoiceRecordingPreview';

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  margin: theme.spacing(0, 1.5),
}));

const Pill = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1.25),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '24px',
  backgroundColor:
    theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.background.paper,
}));

/** WhatsApp-style M:SS (no leading zero on minutes when 0) */
function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const VoiceRecordingPanel = ({
  recordingTime,
  waveformHeights,
  isPaused = false,
  previewRecordingUrl = null,
  previewWaveformHeights = [],
  onCancel,
  onSend,
  onPause,
  onResume,
}) => {
  return (
    <Row>
      <IconButton
        type="button"
        size="small"
        onClick={onCancel}
        aria-label={I18n.t('SendMessageForm.Discard recording')}
        sx={theme => ({
          color: theme.palette.text.secondary,
          padding: theme.spacing(0.75),
        })}
      >
        <DeleteOutlineOutlinedIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Pill>
        {isPaused ? (
          <PausedVoiceRecordingPreview
            recordingTime={recordingTime}
            previewUrl={previewRecordingUrl}
            previewHeights={previewWaveformHeights}
            liveWaveformHeights={waveformHeights}
          />
        ) : (
          <>
            <Box
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                flexShrink: 0,
              })}
            >
              <FiberManualRecordIcon
                sx={theme => ({
                  fontSize: 10,
                  color: theme.palette.error.main,
                  animation: `${blink} 1s ease-in-out infinite`,
                })}
              />
              <Typography
                component="span"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 13,
                  color: 'text.secondary',
                  minWidth: 36,
                  flexShrink: 0,
                }}
              >
                {formatTime(recordingTime)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LiveWaveformStrip heights={waveformHeights} />
            </Box>
          </>
        )}
      </Pill>

      <IconButton
        type="button"
        size="small"
        onClick={isPaused ? onResume : onPause}
        aria-label={
          isPaused
            ? I18n.t('SendMessageForm.Resume recording')
            : I18n.t('SendMessageForm.Pause recording')
        }
        sx={theme => ({
          color: theme.palette.text.secondary,
          padding: theme.spacing(0.75),
        })}
      >
        {isPaused ? <MicIcon sx={{ fontSize: 26 }} /> : <PauseIcon sx={{ fontSize: 28 }} />}
      </IconButton>

      <IconButton
        type="button"
        onClick={() => {
          void onSend();
        }}
        aria-label={I18n.t('SendMessageForm.Send voice message')}
        sx={theme => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          width: 40,
          height: 40,
          padding: 0,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
          },
        })}
      >
        <SendIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Row>
  );
};

VoiceRecordingPanel.propTypes = {
  recordingTime: number.isRequired,
  waveformHeights: arrayOf(number).isRequired,
  isPaused: bool,
  previewRecordingUrl: string,
  previewWaveformHeights: arrayOf(number),
  onCancel: func.isRequired,
  onSend: func.isRequired,
  onPause: func.isRequired,
  onResume: func.isRequired,
};

export default VoiceRecordingPanel;
